import React, { useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  BarChart2,
  LogOut,
  Plus,
  ChevronRight,
  Pencil,
  Shield,
  Cpu,
  Map,
  Sparkles,
  PanelLeftClose,
  PanelRight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { cn } from '../lib/utils';
import { useDispatch } from 'react-redux';
import { signOutSaga } from '../redux/actions/authActions';
import crcEvansLogo from '../assets/crc-evans-logo.png';
import crcEvansLogoArchive from '../assets/crc-evans-logo-archive.png';
import { useUserPermissionsAfterLogin } from '../features/admin/hooks/useUserPermissionsAfterLogin';
import { BACKEND_VERSION } from '../version';
import { BUILD_VERSION, GIT_COMMIT_SHORT } from '../buildInfo.generated';
import { NEW_FEATURES, ENHANCEMENTS, BUG_FIXES } from '../constants/releaseNotes';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  onCreateClick?: () => void;
  onEditClick?: () => void;
  showBackNavigation?: boolean;
  mode?: 'create' | 'edit' | 'default';
  onBack?: () => void;
  setShowBackNavigation?: (showBackNavigation: boolean) => void;
}

const SIDEBAR_COMPACT_KEY = 'data360.sidebarCompact';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  key?: string;
  /** When true, NavLink uses `end` so only exact path matches (e.g. /Map). */
  end?: boolean;
}

const navLinkClass = (isActive: boolean, sidebarCompact: boolean) =>
  [
    'flex items-center gap-3 rounded-lg text-[13px] font-medium transition-all duration-150 py-2.5',
    sidebarCompact
      ? 'px-0 justify-center lg:justify-center'
      : 'px-0 justify-center lg:px-3 lg:justify-start',
    isActive
      ? 'bg-hero-red/15 text-white'
      : 'text-white/50 hover:text-white/80 hover:bg-white/5',
  ].join(' ');

const Layout: React.FC<LayoutProps> = ({
  children,
  title = 'Projects',
  subtitle,
  onCreateClick,
  onEditClick,
  showBackNavigation = false,
  onBack,
  setShowBackNavigation,
}) => {
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [isReleaseNotesOpen, setIsReleaseNotesOpen] = React.useState(false);
  const [sidebarCompact, setSidebarCompact] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const backendVersion = BACKEND_VERSION;

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { hasPermission } = useUserPermissionsAfterLogin();

  const firstName: string = user?.['custom:first_name'] || '';
  const lastName: string = user?.['custom:last_name'] || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() ||
    (firstName.slice(0, 2) || 'U').toUpperCase();
  const roleLabel: string = user?.['custom:role'] || user?.role || '';

  const navItems: NavItem[] = [
    {
      label: 'Projects',
      icon: <Home className="h-[18px] w-[18px]" />,
      href: '/Projects',
      key: 'view_projects',
    },
    {
      label: 'Pipe Inventory',
      icon: <BarChart2 className="h-[18px] w-[18px]" />,
      href: '/PipeInventory',
      key: 'view_pipe_inventory',
    },
    {
      label: 'Map',
      icon: <Map className="h-[18px] w-[18px]" />,
      href: '/Map',
      key: 'view_map',
      end: true,
    },
    {
      label: 'Devices',
      icon: <Cpu className="h-[18px] w-[18px]" />,
      href: '/Devices',
      key: 'view_devices',
      end: true,
    },
    {
      label: 'Admin',
      icon: <Shield className="h-[18px] w-[18px]" />,
      href: '/Admin',
      key: 'manage_admins',
      end: true,
    },
  ];

  useEffect(() => {
    try {
      setSidebarCompact(localStorage.getItem(SIDEBAR_COMPACT_KEY) === '1');
    } catch {
      /* ignore */
    }
  }, []);

  const toggleSidebarCompact = () => {
    setSidebarCompact((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COMPACT_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    return () => {
      setIsLoggingOut(false);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === '/login') {
      setIsLoggingOut(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    if (isLoggingOut) return;
    try {
      setIsLoggingOut(true);
      // Do not clear tokens here — signOut saga writes AUTH_LOGOUT audit first,
      // then clears tokens. ProtectedRoute redirects to /login after SIGNOUT.
      dispatch(signOutSaga());
    } catch {
      setIsLoggingOut(false);
    }
  };

  const pageTitle =
    location.pathname.startsWith('/Projects')
      ? 'Projects'
      : location.pathname === '/Devices'
        ? 'Devices'
        : location.pathname === '/PipeInventory' || location.pathname === '/Analysis'
          ? 'Pipe Inventory'
          : location.pathname === '/Map'
            ? 'Map'
            : location.pathname === '/Admin'
              ? 'Admin'
              : title;

  const renderReleaseSection = (
    heading: string,
    items: string[],
    accentClass: 'border-t-hero-red' | 'border-t-galvanizing-green' | 'border-t-thin-yellow',
  ) => (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-[#E2E5EA] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]',
        'border-t-[3px]',
        accentClass,
      )}
    >
      <div className="px-4 py-3">
        <h4 className="font-heading text-[14px] font-semibold text-blueprint-blue">{heading}</h4>
        <ul className="mt-3 space-y-2">
          {items.map((item, index) => (
            <li key={index} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-text-grey">
              <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-hero-red" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderReleaseNotesContent = () => (
    <div className="space-y-4">
      {renderReleaseSection('New Features', NEW_FEATURES, 'border-t-hero-red')}
      {renderReleaseSection('Enhancements', ENHANCEMENTS, 'border-t-galvanizing-green')}
      {renderReleaseSection('Bug Fixes', BUG_FIXES, 'border-t-thin-yellow')}
    </div>
  );

  return (
    <div className="min-h-screen h-screen flex bg-page-bg overflow-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 bottom-0 z-50 flex w-14 flex-col overflow-hidden bg-blueprint-blue transition-[width] duration-200 ease-out',
          sidebarCompact ? 'lg:w-14' : 'lg:w-60'
        )}
      >
        {/* Brand header */}
        <div className="flex h-[60px] flex-shrink-0 items-center justify-center gap-2 border-b border-white/10 px-2 lg:px-3">
          <div
            className={cn(
              'flex min-w-0 flex-1 items-center gap-3',
              sidebarCompact ? 'justify-center' : 'justify-center lg:justify-start'
            )}
          >
            <img
              src={crcEvansLogo}
              alt="CRC Evans"
              className="h-8 w-8 flex-shrink-0 object-contain"
            />
            <div className={cn('min-w-0', !sidebarCompact ? 'hidden lg:block' : 'hidden')}>
              <div className="font-heading text-[14px] font-semibold leading-tight text-white">
                DATA 360
              </div>
              <div className="text-[11px] text-white/40">CRC Evans Platform</div>
            </div>
          </div>
          {/* <button
            type="button"
            onClick={toggleSidebarCompact}
            className="hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/5 hover:text-white lg:inline-flex"
            aria-label={sidebarCompact ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCompact ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCompact ? (
              <PanelRight className="h-[18px] w-[18px] mr-2" />
            ) : (
              <PanelLeftClose className="h-[18px] w-[18px]" />
            )}
          </button> */}
        </div>

        {/* Navigation */}
        <nav
          className={cn(
            'flex flex-1 flex-col gap-1 overflow-y-auto py-3',
            sidebarCompact ? 'px-2 lg:px-2' : 'px-2 lg:px-3'
          )}
        >
          {navItems
            .filter((item) => hasPermission(item.key || ''))
            .map((item) => (
              <NavLink
                key={item.label}
                to={item.href}
                end={item.end}
                title={item.label}
                className={({ isActive }) => navLinkClass(isActive, sidebarCompact)}
                onClick={() => {
                  if (item.href === '/Projects' && onBack) {
                    onBack();
                  }
                  setShowBackNavigation?.(false);
                }}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                <span className={cn(!sidebarCompact ? 'hidden lg:inline' : 'hidden')}>{item.label}</span>
              </NavLink>
            ))}
        </nav>

        {/* Footer stack: user → What's New → Logout → version */}
        <div className="flex-shrink-0 border-t border-white/10">
          {/* User profile */}
          <div className={cn('py-3', sidebarCompact ? 'px-2 lg:px-2' : 'px-2 lg:px-3')}>
            <div
              className={cn(
                'flex items-center gap-3 rounded-lg py-2',
                sidebarCompact ? 'justify-center px-0 lg:px-0' : 'justify-center px-0 lg:justify-start lg:px-2'
              )}
              title={fullName || 'User'}
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-hero-red">
                <span className="font-heading text-[12px] font-semibold text-white">{initials}</span>
              </div>
              <div className={cn('min-w-0 flex-1', !sidebarCompact ? 'hidden lg:block' : 'hidden')}>
                <div className="truncate text-[13px] font-medium text-white">{fullName || 'User'}</div>
                {roleLabel && (
                  <div className="truncate text-[11px] text-white/40">{roleLabel}</div>
                )}
              </div>
            </div>
          </div>

          {/* What's New */}
          <div className={cn(sidebarCompact ? 'px-2 lg:px-2' : 'px-2 lg:px-3')}>
            <button
              type="button"
              className={cn(
                'mb-2 flex w-full items-center gap-3 rounded-lg border border-white/15 text-[13px] font-medium text-white/75 transition-all duration-150 hover:bg-white/5 hover:text-white',
                sidebarCompact
                  ? 'justify-center px-0 py-2 lg:justify-center lg:px-0'
                  : 'justify-center px-0 py-2 lg:justify-start lg:px-3'
              )}
              onClick={() => setIsReleaseNotesOpen(true)}
              title="What's New"
            >
              <Sparkles className="h-4 w-4 flex-shrink-0" />
              <span className={cn(!sidebarCompact ? 'hidden lg:inline' : 'hidden')}>What&apos;s New</span>
            </button>
          </div>

          {/* Logout */}
          <div className={cn('pb-2 pt-1', sidebarCompact ? 'px-2 lg:px-2' : 'px-2 lg:px-3')}>
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-3 rounded-lg text-[13px] font-medium text-hero-red-30 transition-all duration-150 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50',
                sidebarCompact
                  ? 'justify-center px-0 py-2 lg:justify-center lg:px-0'
                  : 'justify-center px-0 py-2 lg:justify-start lg:px-3'
              )}
              onClick={handleLogout}
              disabled={isLoggingOut}
              title={isLoggingOut ? 'Signing out…' : 'Logout'}
            >
              {isLoggingOut ? (
                <div className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-hero-red-30 border-t-transparent" />
              ) : (
                <LogOut className="h-4 w-4 flex-shrink-0" />
              )}
              <span className={cn(!sidebarCompact ? 'hidden lg:inline' : 'hidden')}>
                {isLoggingOut ? 'Signing out…' : 'Logout'}
              </span>
            </button>
          </div>

          {/* Version + Powered by */}
          <div
            className={cn(
              'border-t border-white/10 px-4 py-3',
              !sidebarCompact ? 'hidden lg:block' : 'hidden'
            )}
          >
            <div className="mb-5 text-center text-[10px] text-white/25">
              Build {BUILD_VERSION} ({GIT_COMMIT_SHORT})
            </div>
            <div className="mb-1 text-center text-[10px] text-white/25">Powered by</div>
            <a
              href="https://www.crcevans.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-3 flex items-center justify-center opacity-80 transition-opacity hover:opacity-100"
            >
              <img src={crcEvansLogoArchive} alt="CRC Evans" className="h-7 w-auto object-contain" />
            </a>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div
        className={cn(
          'ml-14 flex min-h-0 min-w-0 flex-1 flex-col transition-[margin] duration-200 ease-out',
          sidebarCompact ? 'lg:ml-14' : 'lg:ml-60'
        )}
      >
        {/* Header */}
        <header className="h-[60px] bg-white/80 backdrop-blur-xl border-b border-[#E2E5EA] flex-shrink-0 sticky top-0 z-40">
          <div className="h-full px-4 lg:px-8 flex items-center justify-between gap-4">
            <div className="flex flex-col min-w-0">
              {showBackNavigation ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (onBack) {
                        onBack();
                        navigate('/Projects');
                      }
                    }}
                    className="font-heading text-[18px] font-semibold text-blueprint-blue hover:text-hero-red transition-colors"
                    title="Back to projects"
                  >
                    Projects
                  </button>
                  <ChevronRight className="h-4 w-4 text-heritage-grey flex-shrink-0 mt-1" />
                  <span className="font-heading text-[18px] font-semibold text-blueprint-blue truncate">
                    {subtitle}
                  </span>
                </div>
              ) : (
                <>
                  <h1 className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
                    {pageTitle}
                  </h1>
                  {subtitle && (
                    <p className="text-[11px] text-heritage-grey truncate">{subtitle}</p>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {!showBackNavigation && onCreateClick && hasPermission('create_projects') && (
                <button
                  type="button"
                  onClick={onCreateClick}
                  className="flex items-center gap-2 bg-hero-red text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-hero-red-60 transition-all duration-150"
                >
                  <Plus className="h-4 w-4" />
                  Create Project
                </button>
              )}
              {showBackNavigation && onEditClick && hasPermission('edit_projects') && (
                <button
                  type="button"
                  onClick={onEditClick}
                  className="flex items-center gap-2 bg-hero-red text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-hero-red-60 transition-all duration-150"
                >
                  <Pencil className="h-4 w-4" />
                  Edit Project
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 min-h-[calc(100vh-60px)] min-w-0 overflow-y-auto overflow-x-hidden px-2 sm:px-4 flex flex-col">
          <div className="pt-4 pb-0 flex-1 flex flex-col min-h-0 min-w-0 w-full max-w-full">{children}</div>
        </main>
      </div>

      {/* What's New modal — CRCE design system */}
      <Dialog open={isReleaseNotesOpen} onOpenChange={setIsReleaseNotesOpen}>
        <DialogContent className="flex w-[95vw] max-w-4xl max-h-[90vh] flex-col gap-0 overflow-hidden rounded-xl bg-white p-0">
          <div className="flex flex-shrink-0 items-center gap-3 border-b border-[#E2E5EA] px-6 py-4 pr-14">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-hero-red-10">
              <Sparkles className="h-5 w-5 text-hero-red" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="font-heading text-[18px] font-semibold leading-tight text-blueprint-blue">
                What&apos;s New
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[13px] text-heritage-grey">
                Latest platform updates from CRC Evans
              </DialogDescription>
            </div>
          </div>

          <div className="flex-1 space-y-6 overflow-y-auto p-6">
            <div className="overflow-hidden rounded-xl border border-[#E2E5EA] border-t-[3px] border-t-blueprint-blue bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <div className="px-4 py-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-heritage-grey">
                  Current versions
                </h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-[#EEF0F3] bg-page-bg px-3 py-2.5">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-heritage-grey">
                      Frontend
                    </p>
                    <p className="mt-1 font-heading text-[14px] font-semibold text-blueprint-blue">
                      v{BUILD_VERSION}
                      <span className="ml-1 text-[12px] font-medium text-heritage-grey">
                        ({GIT_COMMIT_SHORT})
                      </span>
                    </p>
                  </div>
                  <div className="rounded-lg border border-[#EEF0F3] bg-page-bg px-3 py-2.5">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-heritage-grey">
                      Backend
                    </p>
                    <p className="mt-1 font-heading text-[14px] font-semibold text-blueprint-blue">
                      v{backendVersion}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">
                  Recent updates
                </h3>
                <p className="mt-1 text-[13px] text-heritage-grey">
                  New capabilities, improvements, and fixes—UI follows the CRC Evans (CRCE)
                  design system.
                </p>
              </div>
              {renderReleaseNotesContent()}
            </div>

            <div className="flex items-center justify-center gap-2 border-t border-[#EEF0F3] pt-4">
              <span className="text-[10px] font-medium uppercase tracking-wider text-heritage-grey">
                Powered by
              </span>
              <img
                src={crcEvansLogoArchive}
                alt="CRC Evans"
                className="h-5 w-auto object-contain opacity-80"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
